import { Input } from "@chakra-ui/react";
import { FieldProps, RJSFSchema, asNumber } from "@rjsf/utils";
import { useCallback, useState } from "react";

import { FormContext } from "../process-run-form";

// Original: https://github.com/rjsf-team/react-jsonschema-form/blob/v5.21.1/packages/core/src/components/fields/NumberField.tsx

// Replicated from the built-in NumberField to preserve trailing-decimal/zero UX
// (e.g. "1." and "1.0" must not be immediately converted to "1" while typing).
const trailingCharMatcherWithPrefix = /\.([0-9]*0)*$/;
const trailingCharMatcher = /[0.]0*$/;

/**
 * NumberField override that disables scroll-to-change on number inputs by
 * blurring the field on wheel events.
 * Registered as FIELDS.NumberField so it applies to all number/integer fields.
 */
export function NumberField(props: FieldProps<number, RJSFSchema, FormContext>) {
  const { idSchema, formData, onChange, onBlur, onFocus, disabled, readonly, autofocus } = props;
  const id = idSchema.$id;

  const [lastValue, setLastValue] = useState<string | number | undefined>(formData);

  // Preserve a trailing "." or trailing zeros while the user is still typing
  let displayValue: string | number | undefined = formData;
  if (typeof lastValue === "string" && typeof displayValue === "number") {
    const re = new RegExp(`^(${String(displayValue).replace(".", "\\.")})?\\.?0*$`);
    if (lastValue.match(re)) {
      displayValue = lastValue;
    }
  }

  const handleChange = useCallback(
    (raw: string) => {
      setLastValue(raw);
      // If the raw value ends with a "." or with trailing zeros after a ".",
      // we want to preserve it as a string for now, otherwise we convert it to a number (if possible)
      const prefixed = raw.charAt(0) === "." ? `0${raw}` : raw;
      const processed = prefixed.match(trailingCharMatcherWithPrefix)
        ? asNumber(prefixed.replace(trailingCharMatcher, ""))
        : asNumber(prefixed);
      onChange(Number(processed));
    },
    [onChange]
  );

  return (
    <Input
      id={id}
      name={id}
      type="number"
      value={displayValue ?? ""}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={(e) => onBlur(id, e.target.value)}
      onFocus={(e) => onFocus(id, e.target.value)}
      onWheel={(e) => e.currentTarget.blur()}
      autoFocus={autofocus}
      isDisabled={disabled || readonly}
      isReadOnly={readonly}
    />
  );
}
